import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Trash2, Tag } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPromo = () => {
  const { hasPermission } = useAuth();
  const canAccess = hasPermission('promo');
  const navigate = useNavigate();
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    discount_percent: '',
    min_purchase: '',
    max_uses: 100,
    max_uses_per_user: 1,
    expires_at: '',
    is_active: true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchPromos = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/promo`);
      setPromos(response.data);
    } catch (err) {
      console.error('Failed to fetch promo codes:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canAccess) {
      navigate('/connexion');
      return;
    }
    fetchPromos();
  }, [canAccess, navigate, fetchPromos]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const payload = {
        code: formData.code.toUpperCase(),
        discount_percent: parseFloat(formData.discount_percent),
        min_purchase: parseFloat(formData.min_purchase) || 0,
        max_uses: parseInt(formData.max_uses) || 100,
        max_uses_per_user: parseInt(formData.max_uses_per_user) || 0,
        expires_at: formData.expires_at || null,
        is_active: formData.is_active
      };

      await axios.post(`${API_URL}/api/promo`, payload, {
        
      });

      setFormData({
        code: '',
        discount_percent: '',
        min_purchase: '',
        max_uses: 100,
        max_uses_per_user: 1,
        expires_at: '',
        is_active: true
      });
      setShowForm(false);
      fetchPromos();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Erreur lors de la création');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (promoId) => {
    if (!window.confirm('Supprimer ce code promo ?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/promo/${promoId}`, {
        
      });
      setPromos(promos.filter(p => p.id !== promoId));
    } catch (error) {
      // handled by toast
      alert('Erreur lors de la suppression');
    }
  };

  if (!canAccess) return null;

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/admin" className="text-zinc-500 hover:text-white text-sm mb-2 inline-block">
              ← Retour au dashboard
            </Link>
            <h1 
              className="text-4xl text-white font-light"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Codes Promo
            </h1>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="btn-primary flex items-center gap-2"
            data-testid="add-promo-btn"
          >
            <Plus className="w-4 h-4" />
            Nouveau Code
          </button>
        </div>

        {/* Form */}
        {showForm && (
          <div className="bg-[color:var(--color-surface)] border border-white/10 p-6 mb-8 animate-fade-in">
            <h3 className="text-white mb-4">Créer un code promo</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="input-label">Code *</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    required
                    className="w-full input-field uppercase"
                    placeholder="EX: PROMO20"
                    data-testid="promo-code-input"
                  />
                </div>
                <div>
                  <label className="input-label">Réduction (%) *</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={formData.discount_percent}
                    onChange={(e) => setFormData({ ...formData, discount_percent: e.target.value })}
                    required
                    className="w-full input-field"
                    placeholder="20"
                    data-testid="promo-discount-input"
                  />
                </div>
                <div>
                  <label className="input-label">Achat minimum (€)</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.min_purchase}
                    onChange={(e) => setFormData({ ...formData, min_purchase: e.target.value })}
                    className="w-full input-field"
                    placeholder="0"
                    data-testid="promo-min-input"
                  />
                </div>
                <div>
                  <label className="input-label">Utilisations max (global)</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.max_uses}
                    onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                    className="w-full input-field"
                    placeholder="100"
                    data-testid="promo-max-uses-input"
                  />
                  <p className="text-xs text-zinc-500 mt-1">Total combiné tous clients confondus</p>
                </div>
                <div>
                  <label className="input-label">Utilisations max par client</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.max_uses_per_user}
                    onChange={(e) => setFormData({ ...formData, max_uses_per_user: e.target.value })}
                    className="w-full input-field"
                    placeholder="1"
                    data-testid="promo-max-per-user-input"
                  />
                  <p className="text-xs text-zinc-500 mt-1">0 = illimité par client · 1 = usage unique</p>
                </div>
                <div>
                  <label className="input-label">Date d'expiration</label>
                  <input
                    type="datetime-local"
                    value={formData.expires_at}
                    onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                    className="w-full input-field"
                    data-testid="promo-expires-input"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-5 h-5 accent-[color:var(--color-primary)]"
                      data-testid="promo-active-input"
                    />
                    <span className="text-zinc-400">Actif</span>
                  </label>
                </div>
              </div>

              {error && (
                <p className="text-red-500 text-sm" data-testid="promo-error">{error}</p>
              )}

              <div className="flex justify-end gap-4">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="btn-outline"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary disabled:opacity-50"
                  data-testid="promo-submit-btn"
                >
                  {saving ? 'Création...' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Promo List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={`skeleton-${i}`} className="bg-[color:var(--color-surface)] border border-white/10 h-20 animate-pulse" />
            ))}
          </div>
        ) : promos.length > 0 ? (
          <div className="space-y-4" data-testid="promo-list">
            {promos.map(promo => (
              <div 
                key={promo.id} 
                className="bg-[color:var(--color-surface)] border border-white/10 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                data-testid={`promo-${promo.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[color:rgb(var(--primary-rgb)/0.10)] flex items-center justify-center">
                    <Tag className="w-6 h-6 text-[color:var(--color-primary)]" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-white font-medium">{promo.code}</p>
                      <span className={`px-2 py-0.5 text-xs ${
                        promo.is_active 
                          ? 'text-green-500 bg-green-500/10' 
                          : 'text-zinc-500 bg-zinc-500/10'
                      }`}>
                        {promo.is_active ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-3 text-zinc-500 text-sm mt-1">
                      <span className="text-[color:var(--color-primary)]">-{promo.discount_percent}%</span>
                      {promo.min_purchase > 0 && <span>Min: {promo.min_purchase}€</span>}
                      <span>Global: {promo.used_count}/{promo.max_uses}</span>
                      {promo.max_uses_per_user === 0 ? (
                        <span className="text-green-400">∞ par client</span>
                      ) : (
                        <span>{promo.max_uses_per_user || 1}× par client</span>
                      )}
                      {promo.expires_at && (
                        <span>Expire: {new Date(promo.expires_at).toLocaleDateString('fr-BE')}</span>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => handleDelete(promo.id)}
                  className="p-2 hover:bg-white/10 transition-colors self-end sm:self-auto"
                  data-testid={`delete-promo-${promo.id}`}
                >
                  <Trash2 className="w-4 h-4 text-zinc-400 hover:text-red-500" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-[color:var(--color-surface)] border border-white/10">
            <Tag className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-500 mb-4">Aucun code promo</p>
            <button
              onClick={() => setShowForm(true)}
              className="btn-primary"
            >
              Créer un code
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPromo;
